import React, { useState } from "react";
import { motion } from "framer-motion";

// CARD 1: Consensus Matrix (Random variances aligning to a single unified plane)
const Column = ({ x, y, initialHeight, targetHeight, hovered, delay = 0 }: any) => {
  const height = hovered ? targetHeight : initialHeight;
  
  return (
    <motion.div
      className="absolute"
      style={{ left: x, top: y, width: 32, height: 32, transformStyle: "preserve-3d" }}
      animate={{ translateZ: 0 }} 
      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1], delay }}
    >
      <motion.div
        className="absolute inset-0 border"
        animate={{ 
          translateZ: height,
          borderColor: hovered ? "rgba(16,185,129,0.6)" : "rgba(255,255,255,0.15)",
          backgroundColor: hovered ? "rgba(16,185,129,0.15)" : "#111113"
        }}
        transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1], delay }}
      >
         <div className="absolute inset-0 flex items-center justify-center opacity-50">
           <div className="w-1 h-1 bg-white/30 rounded-full" />
         </div>
      </motion.div>

      <motion.div
        className="absolute bottom-0 left-0 border"
        style={{ width: 32, transformOrigin: "bottom", rotateX: -90 }}
        animate={{ 
          height,
          borderColor: hovered ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.15)",
          backgroundColor: hovered ? "rgba(16,185,129,0.05)" : "#0a0a0c"
        }}
        transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1], delay }}
      />
      <motion.div
        className="absolute top-0 right-0 border"
        style={{ height: 32, transformOrigin: "right", rotateY: 90 }}
        animate={{ 
          width: height,
          borderColor: hovered ? "rgba(16,185,129,0.3)" : "rgba(255,255,255,0.15)",
          backgroundColor: hovered ? "rgba(16,185,129,0.02)" : "#050506"
        }}
        transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1], delay }}
      />
    </motion.div>
  );
};

const ConsensusVisual = ({ hovered }: { hovered?: boolean }) => {
  return (
    <div className="relative w-32 h-32" style={{ transformStyle: "preserve-3d", left: 10, top: 20 }}>
       <Column x={0} y={0} initialHeight={15} targetHeight={45} hovered={hovered} delay={0.1} />
       <Column x={40} y={0} initialHeight={75} targetHeight={45} hovered={hovered} delay={0.05} />
       <Column x={0} y={40} initialHeight={40} targetHeight={45} hovered={hovered} delay={0.15} />
       <Column x={40} y={40} initialHeight={25} targetHeight={45} hovered={hovered} delay={0} />
       
       <motion.div 
         className="absolute border border-[#10b981] shadow-[0_0_15px_rgba(16,185,129,0.4)] bg-[#10b981]/20"
         style={{ left: 16, top: 16, width: 40, height: 40, translateZ: 45 }}
         initial={{ opacity: 0, scale: 0.5 }}
         animate={{ opacity: hovered ? 1 : 0, scale: hovered ? 1 : 0.5 }}
         transition={{ duration: 0.4, delay: hovered ? 0.6 : 0 }}
       />
    </div>
  );
};

// CARD 2: Scanner Matrix (Automated pre-labeling agents passing through layers)
const ScannerVisual = ({ hovered }: { hovered?: boolean }) => {
  return (
    <div className="relative w-32 h-32" style={{ transformStyle: "preserve-3d" }}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 border border-white/20 bg-[#060608]/90"
          animate={{ translateZ: hovered ? (i * 25) : (i * 6) }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
        >
           <div className="w-full h-full opacity-30" style={{ backgroundImage: "radial-gradient(white 1px, transparent 1px)", backgroundSize: "10px 10px", backgroundPosition: "center" }} />
        </motion.div>
      ))}
      
      <motion.div
        className="absolute inset-[-10px] border border-blue-400 bg-blue-500/10 shadow-[0_0_20px_rgba(59,130,246,0.2)] overflow-hidden"
        style={{ transformStyle: "preserve-3d" }}
        initial={{ translateZ: 80, opacity: 0 }}
        animate={{ 
          translateZ: hovered ? -20 : 80, 
          opacity: hovered ? [0, 1, 1, 0] : 0 
        }}
        transition={{ 
          translateZ: { duration: 1.5, ease: "linear", repeat: hovered ? Infinity : 0, repeatType: "reverse" },
          opacity: { duration: 1.5, ease: "linear", repeat: hovered ? Infinity : 0, repeatType: "reverse" }
        }}
      >
        <motion.div 
          className="w-full h-[2px] bg-blue-400 shadow-[0_0_15px_#60a5fa]" 
          animate={{ y: hovered ? [0, 148, 0] : 0 }}
          transition={{ duration: 1.5, ease: "linear", repeat: hovered ? Infinity : 0, repeatType: "reverse" }}
        />
      </motion.div>
    </div>
  );
};

// CARD 3: Audit Vault (Immutable ledger opening to reveal cryptographic core)
const VaultVisual = ({ hovered }: { hovered?: boolean }) => {
  return (
    <div className="relative w-32 h-32" style={{ transformStyle: "preserve-3d" }}>
      <div className="absolute inset-0 border border-white/20 bg-[#060608]" style={{ transform: "translateZ(0px)" }} />
      
      <motion.div
        className="absolute inset-6 bg-violet-500/20 border border-violet-500/60 shadow-[0_0_30px_rgba(139,92,246,0.4)] flex items-center justify-center"
        initial={{ translateZ: 2 }}
        animate={{ 
          translateZ: hovered ? 18 : 2,
          rotateZ: hovered ? 180 : 0
        }}
        transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1], delay: 0.1 }}
      >
         <div className="w-6 h-6 border-2 border-violet-400 rounded-sm rotate-45" />
      </motion.div>

      <div className="absolute inset-0" style={{ transform: "translateZ(30px)", transformStyle: "preserve-3d" }}>
        <motion.div
          className="absolute inset-0 border border-white/30 bg-[#0a0a0c]/95 backdrop-blur-md flex items-center justify-center"
          style={{ transformOrigin: "left" }}
          animate={{ rotateY: hovered ? -110 : 0 }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
        >
          <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center">
            <div className="w-2 h-2 bg-white/40 rounded-full" />
          </div>
        </motion.div>
      </div>
      
      <motion.div className="absolute top-0 left-0 w-full h-[30px] border border-white/20 bg-[#020202]" style={{ transformOrigin: "top", rotateX: 90 }} />
      <motion.div className="absolute top-0 left-0 h-full w-[30px] border border-white/20 bg-[#040404]" style={{ transformOrigin: "left", rotateY: -90 }} />
      <motion.div className="absolute bottom-0 left-0 w-full h-[30px] border border-white/20 bg-[#030303]" style={{ transformOrigin: "bottom", rotateX: -90 }} />
      <motion.div className="absolute top-0 right-0 h-full w-[30px] border border-white/20 bg-[#050506]" style={{ transformOrigin: "right", rotateY: 90 }} />
    </div>
  );
};

const IsometricFeatureCard = ({ title, description, children, label }: any) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex flex-col group cursor-pointer w-full h-full p-8 md:p-12 relative overflow-hidden"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Subtle background glow on hover */}
      <motion.div 
        className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" 
        style={{ filter: "blur(60px)" }} 
      />

      <div className="text-[11px] font-mono text-zinc-600 tracking-wider mb-8 uppercase relative z-10">
        {label}
      </div>
      
      <div className="h-[240px] w-full flex items-center justify-center relative mb-8 z-10">
        <div style={{ perspective: 1200, transformStyle: "preserve-3d" }} className="w-full h-full flex items-center justify-center">
          <motion.div
            style={{ rotateX: 60, rotateZ: -45, transformStyle: "preserve-3d" }}
            animate={{ rotateZ: hovered ? -30 : -45, rotateX: hovered ? 55 : 60 }}
            transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
            className="relative flex items-center justify-center"
          >
            {React.cloneElement(children, { hovered })}
          </motion.div>
        </div>
      </div>
      
      <div className="mt-auto relative z-10">
        <h3 className="text-[17px] font-medium text-white mb-2">{title}</h3>
        <p className="text-[#a1a1aa] text-[15px] leading-[1.6]">{description}</p>
      </div>
    </div>
  );
};

export const ValidationFlowSection = () => {
  return (
    <section className="w-full bg-[#000000] py-24">
      <div className="max-w-7xl mx-auto px-6 mb-16">
        <h2 className="text-3xl md:text-4xl font-medium text-white mb-4">
          Architectural Data Processing.
        </h2>
        <p className="text-zinc-400 text-lg max-w-2xl">
          We rebuilt the entire annotation pipeline into a series of cryptographic, consensus-driven systems that mathematically guarantee zero-slop datasets.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/10 border-y border-white/10 bg-[#020202]">
          
          <IsometricFeatureCard
            label="SYS 0.1"
            title="Consensus Matrix"
            description="Variances are mathematically aligned. Multiple domain experts evaluate independently until cryptographic consensus is achieved."
          >
            <ConsensusVisual />
          </IsometricFeatureCard>

          <IsometricFeatureCard
            label="SYS 0.2"
            title="Automated Validation"
            description="Autonomous pre-labeling agents pass through deep data layers, extracting context while preserving source integrity."
          >
            <ScannerVisual />
          </IsometricFeatureCard>

          <IsometricFeatureCard
            label="SYS 0.3"
            title="Immutable Vault"
            description="Every evaluation, stroke, and annotation is locked into a tamper-evident audit ledger, ready for regulatory review."
          >
            <VaultVisual />
          </IsometricFeatureCard>

        </div>
      </div>
    </section>
  );
};

export default ValidationFlowSection;
